import { Link } from 'react-router-dom'
import { Card } from '@/common/ui/Card/Card'
import type { ProductScoreSchema } from '@/common/api/client/types.gen'
import type { BrandComparisonBrandData } from '../../model/useCompanyStatsBrandComparison'
import { CompanyStatsProductScorePill } from '../CompanyStatsProductScorePill'

type CompanyStatsBrandComparisonProductsProps = {
    brandA: BrandComparisonBrandData
    brandB: BrandComparisonBrandData
}

type ProductListColumnProps = {
    title: string
    titleIcon: string
    products: ProductScoreSchema[]
}

const ProductListColumn = ({ title, titleIcon, products }: ProductListColumnProps) => (
    <div className='flex flex-1 flex-col'>
        <h4 className='mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-white'>
            <span aria-hidden>{titleIcon}</span>
            {title}
        </h4>
        {products.length === 0 ? (
            <p className='text-sm text-base-content/50'>Нет данных</p>
        ) : (
            <ol className='flex flex-col' aria-label={title}>
                {products.map((product, index) => (
                    <li
                        key={product.id}
                        className='grid grid-cols-[1.5rem_minmax(0,1fr)_auto] items-start gap-3 border-b border-white/10 py-3 last:border-b-0 last:pb-0 first:pt-0'
                    >
                        <span className='text-sm tabular-nums text-base-content/40' aria-hidden>
                            {index + 1}
                        </span>
                        <Link
                            to={`/app/products/${product.id}/analytics`}
                            className='min-w-0 text-sm leading-snug text-base-content/80 link link-hover'
                            aria-label={`${product.name}, перейти к аналитике`}
                        >
                            {product.name}
                        </Link>
                        <CompanyStatsProductScorePill score={product.score} maxScore={product.max_score} />
                    </li>
                ))}
            </ol>
        )}
    </div>
)

type BrandProductsCardProps = {
    brand: BrandComparisonBrandData
    topBorderColor: 'green' | 'purple'
}

const BrandProductsCard = ({ brand, topBorderColor }: BrandProductsCardProps) => {
    const topProducts = brand.products.top ?? []
    const bottomProducts = brand.products.bottom ?? []

    return (
        <Card color='primary' className='h-full' topBorderColor={topBorderColor}>
            <div className='mb-4 flex items-center justify-between gap-2'>
                <h3 className='text-xs font-bold uppercase tracking-wide text-white'>{brand.name}</h3>
                <span className='text-sm text-base-content/60'>{brand.common.total_products} товаров</span>
            </div>
            <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                <ProductListColumn title='Лучшие' titleIcon='↑' products={topProducts} />
                <ProductListColumn title='Требуют работы' titleIcon='↓' products={bottomProducts} />
            </div>
        </Card>
    )
}

export const CompanyStatsBrandComparisonProducts = ({ brandA, brandB }: CompanyStatsBrandComparisonProductsProps) => (
    <div className='flex flex-col gap-4'>
        <h3 className='text-xs font-semibold uppercase tracking-wide text-base-content/80'>Товары по брендам</h3>
        <div className='flex flex-col gap-4'>
            <BrandProductsCard brand={brandA} topBorderColor='green' />
            <BrandProductsCard brand={brandB} topBorderColor='purple' />
        </div>
    </div>
)
